import type { DriveStep, Driver } from 'driver.js'
import i18n from 'modules/core/lib/i18n'
import { waitForElement } from './tourUtils'

function getPreviousMonthRoute(): string {
  const now = new Date()
  let year = now.getFullYear()
  let month = now.getMonth()
  if (month === 0) {
    month = 12
    year -= 1
  }
  return `/attendance/time-tracking/list/monthly/${year}/${month}/1`
}

export function getTaskRoute(taskKey: string): string {
  const routes: Record<string, () => string> = {
    filter_important: getPreviousMonthRoute,
    detect_alerts: getPreviousMonthRoute,
    bulk_approve: getPreviousMonthRoute,
  }
  return routes[taskKey]?.() ?? '/attendance/time-tracking'
}

export interface TourCallbacks {
  onShowConfetti: () => void
  onShowBookingModal: () => void
  navigate: (path: string) => void
  skipReset: () => void
  resumeTour: (task: string, step: number) => void
}

export function preTagElements(taskKey: string): void {
  if (taskKey === 'filter_important') {
    const sidebarLink = document.querySelector<HTMLElement>(
      'nav a[href*="/attendance/time-tracking"]'
    )
    if (sidebarLink) sidebarLink.setAttribute('data-tour', 'sidebar-time-tracking')

    const alertsBtn = findAlertsButton()
    if (alertsBtn) alertsBtn.setAttribute('data-tour', 'alerts-filter')
  }

  if (taskKey === 'detect_alerts') {
    const chevron = document.querySelector<HTMLElement>(
      '[data-intercom-target="attendance-row-toggle"]'
    )
    if (chevron) chevron.setAttribute('data-tour', 'day-row-chevron')
  }

  if (taskKey === 'bulk_approve') {
    const pendingLabel = findPendingPresetLabel()
    if (pendingLabel) pendingLabel.setAttribute('data-tour', 'pending-preset')

    const checkbox = document.querySelector<HTMLElement>(
      'thead [role="checkbox"], thead input[type="checkbox"]'
    )
    if (checkbox) checkbox.setAttribute('data-tour', 'select-all-checkbox')
  }
}

function findFiltersButton(): HTMLButtonElement | null {
  const allButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('button'))
  for (const btn of allButtons) {
    const label = btn.getAttribute('aria-label')?.toLowerCase() ?? ''
    const text = btn.textContent?.trim().toLowerCase() ?? ''
    if (label.includes('filter') || text === 'filters' || text === 'filtros' || text === 'filter') {
      return btn
    }
  }
  return null
}

function findApplyButton(): HTMLButtonElement | null {
  const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('button'))
  for (const btn of buttons) {
    const text = btn.textContent?.trim().toLowerCase() ?? ''
    if (/^(apply filters?|aplicar filtros?)$/i.test(text)) return btn
  }
  return null
}

function bumpRadixPopoverZIndex(): void {
  const wrapper = document.querySelector<HTMLElement>('[data-radix-popper-content-wrapper]')
  if (wrapper) {
    wrapper.style.zIndex = '100002'
  }
}

function restoreRadixPopoverZIndex(): void {
  const wrapper = document.querySelector<HTMLElement>('[data-radix-popper-content-wrapper]')
  if (wrapper) {
    wrapper.style.zIndex = ''
  }
}

function tagOpenTimesheetButton(): void {
  const dialog = document.querySelector<HTMLElement>('[role="dialog"][data-state="open"]')
  if (!dialog) return
  const buttons = Array.from(dialog.querySelectorAll<HTMLButtonElement>('button'))
  const openTimesheetBtn = buttons.find((btn) => {
    const label = btn.getAttribute('aria-label')?.toLowerCase() ?? ''
    const text = btn.textContent?.trim().toLowerCase() ?? ''
    return (
      label.includes('hoja') ||
      label.includes('timesheet') ||
      text.includes('hoja') ||
      text.includes('timesheet')
    )
  })
  if (openTimesheetBtn) {
    openTimesheetBtn.setAttribute('data-tour', 'open-timesheet-btn')
  }
}

function findAlertsButton(): HTMLButtonElement | null {
  const alertsText = i18n.t('time_tracking.table_collection.filters.alerts')
  const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('button'))
  for (const btn of buttons) {
    const text = btn.textContent?.trim() ?? ''
    if (text === alertsText || text.includes(alertsText)) return btn
  }
  return null
}

function setReactInputValue(input: HTMLInputElement, value: string): void {
  const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
  if (nativeSetter) {
    nativeSetter.call(input, value)
  } else {
    input.value = value
  }
  input.dispatchEvent(new Event('input', { bubbles: true }))
  input.dispatchEvent(new Event('change', { bubbles: true }))
  input.dispatchEvent(new Event('blur', { bubbles: true }))
}

function findPendingPresetLabel(): HTMLElement | null {
  const labels = Array.from(document.querySelectorAll<HTMLElement>('label, button, [role="tab"]'))
  for (const el of labels) {
    const text = el.textContent?.trim().toLowerCase() ?? ''
    if (text.startsWith('pendiente') || text.startsWith('pending')) return el
  }
  return null
}

function findApprovedPresetLabel(): HTMLElement | null {
  const labels = Array.from(document.querySelectorAll<HTMLElement>('label, button, [role="tab"]'))
  for (const el of labels) {
    const text = el.textContent?.trim().toLowerCase() ?? ''
    if (text.startsWith('aprobado') || text.startsWith('approved')) return el
  }
  return null
}

type TaskStepsFactory = (driverInstance: Driver, callbacks: TourCallbacks) => DriveStep[]

const filterImportantSteps: TaskStepsFactory = (driverObj) => [
  {
    element: 'nav a[href*="/attendance/time-tracking"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_sidebar_title'),
      description: i18n.t('growth.get_started.demo_tour.step_sidebar_description'),
      side: 'right' as const,
      align: 'center' as const,
    },
    onHighlightStarted: () => {
      const link = document.querySelector<HTMLElement>('nav a[href*="/attendance/time-tracking"]')
      if (link) {
        link.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
  },
  {
    element: 'tbody',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_table_title'),
      description: i18n.t('growth.get_started.demo_tour.step_table_description'),
      side: 'top' as const,
      align: 'center' as const,
      onNextClick: () => {
        const btn = findFiltersButton()
        if (btn) {
          btn.setAttribute('data-tour', 'filters-button')
        }
        driverObj.moveNext()
      },
    },
    onHighlightStarted: () => {
      const tbody = document.querySelector('tbody')
      if (tbody) tbody.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
  },
  {
    element: '[data-tour="filters-button"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_filters_title'),
      onNextClick: () => {
        const btn = findFiltersButton()
        if (btn) btn.click()
        setTimeout(() => {
          const alertsBtn = findAlertsButton()
          if (alertsBtn) {
            alertsBtn.setAttribute('data-tour', 'alerts-filter')
          }
          bumpRadixPopoverZIndex()
          setTimeout(() => driverObj.moveNext(), 300)
        }, 500)
      },
    },
    onHighlightStarted: () => {
      const btn = findFiltersButton()
      if (btn) {
        btn.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
  },
  {
    element: '[data-tour="alerts-filter"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_alerts_title'),
      description: i18n.t('growth.get_started.demo_tour.step_alerts_description'),
      onNextClick: () => {
        const alertsBtn = findAlertsButton()
        if (alertsBtn) alertsBtn.click()
        void waitForElement('#select-all', 2000)
          .then(() => {
            setTimeout(() => {
              const selectAll = document.querySelector<HTMLElement>('#select-all')
              if (selectAll) selectAll.click()

              setTimeout(() => {
                const applyBtn = findApplyButton()
                if (applyBtn) applyBtn.click()
                restoreRadixPopoverZIndex()
                setTimeout(() => driverObj.moveNext(), 500)
              }, 300)
            }, 300)
          })
          .catch(() => driverObj.moveNext())
      },
    },
    onHighlightStarted: () => {
      bumpRadixPopoverZIndex()
    },
  },
  {
    element: 'tbody',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_done_title'),
      description: i18n.t('growth.get_started.demo_tour.step_done_description'),
    },
    onHighlightStarted: () => {
      restoreRadixPopoverZIndex()
      const tbody = document.querySelector('tbody')
      if (tbody) tbody.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
  },
]

const detectAlertsSteps: TaskStepsFactory = (driverObj, callbacks) => [
  {
    element: 'tbody tr',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_sidepanel_title'),
      description: i18n.t('growth.get_started.demo_tour.step_sidepanel_description'),
      onNextClick: () => {
        const firstRow = document.querySelector<HTMLElement>('tbody tr')
        const detailsBtn = firstRow?.querySelector<HTMLElement>('td:nth-last-child(2) button')
        if (detailsBtn) detailsBtn.click()

        void waitForElement('[role="dialog"][data-state="open"]', 3000)
          .then(() => {
            setTimeout(() => {
              tagOpenTimesheetButton()
              const openBtn = document.querySelector<HTMLElement>(
                '[data-tour="open-timesheet-btn"]'
              )
              if (openBtn) {
                callbacks.skipReset()
                driverObj.destroy()
                try {
                  sessionStorage.setItem(
                    'demo-tour-resume',
                    JSON.stringify({ task: 'detect_alerts', step: 1 })
                  )
                } catch {}
                openBtn.click()
              } else {
                driverObj.destroy()
              }
            }, 600)
          })
          .catch(() => driverObj.destroy())
      },
    },
    onHighlightStarted: () => {
      const row = document.querySelector<HTMLElement>('tbody tr')
      if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
  },
  {
    element: '[data-tour="day-row-chevron"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_expand_day_title'),
      description: i18n.t('growth.get_started.demo_tour.step_expand_day_description'),
      onNextClick: () => {
        const chevron = document.querySelector<HTMLElement>('[data-tour="day-row-chevron"]')
        if (chevron) chevron.click()
        void waitForElement('input[type="text"][inputmode="numeric"]', 2000)
          .then(() => {
            setTimeout(() => driverObj.moveNext(), 300)
          })
          .catch(() => driverObj.moveNext())
      },
    },
    onHighlightStarted: () => {
      const chevron = document.querySelector<HTMLElement>(
        '[data-intercom-target="attendance-row-toggle"]'
      )
      if (chevron) {
        chevron.setAttribute('data-tour', 'day-row-chevron')
        chevron.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
  },
  {
    element: '[data-tour="clock-out-input"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_shift_title'),
      description: i18n.t('growth.get_started.demo_tour.step_shift_description'),
      onNextClick: () => {
        const input = document.querySelector<HTMLInputElement>('[data-tour="clock-out-input"]')
        if (input) {
          setReactInputValue(input, '18:00')
        }
        setTimeout(() => {
          callbacks.onShowConfetti()
          callbacks.onShowBookingModal()
        }, 800)
      },
    },
    onHighlightStarted: () => {
      const inputs = Array.from(
        document.querySelectorAll('input[type="text"][inputmode="numeric"]')
      )
      const emptyInput = inputs.find(
        (inp) => inp instanceof HTMLInputElement && (!inp.value || inp.value === '')
      )
      if (emptyInput) {
        emptyInput.setAttribute('data-tour', 'clock-out-input')
        emptyInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
  },
]

const bulkApproveSteps: TaskStepsFactory = (driverObj, callbacks) => [
  {
    element: '[data-tour="pending-preset"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_pending_title'),
      description: i18n.t('growth.get_started.demo_tour.step_pending_description'),
      onNextClick: () => {
        const label = findPendingPresetLabel()
        if (label) label.click()
        setTimeout(() => driverObj.moveNext(), 800)
      },
    },
    onHighlightStarted: () => {
      const label = findPendingPresetLabel()
      if (label) {
        label.setAttribute('data-tour', 'pending-preset')
        label.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
  },
  {
    element: '[data-tour="select-all-checkbox"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_select_all_title'),
      description: i18n.t('growth.get_started.demo_tour.step_select_all_description'),
      onNextClick: () => {
        const checkbox = document.querySelector<HTMLElement>('[data-tour="select-all-checkbox"]')
        if (checkbox) checkbox.click()
        void waitForElement('[data-testid="button-main"]', 2000)
          .then(() => {
            setTimeout(() => {
              const approveBtns = Array.from(
                document.querySelectorAll('[data-testid="button-main"]')
              )
              for (const btn of approveBtns) {
                const el = btn instanceof HTMLElement ? btn : null
                if (el && el.offsetWidth > 0 && el.offsetHeight > 0) {
                  el.setAttribute('data-tour', 'bulk-approve-btn')
                  const bar =
                    el.parentElement?.closest('.fixed.bottom-2') ?? el.closest('.fixed.bottom-2')
                  if (bar instanceof HTMLElement) bar.style.zIndex = '100002'
                  break
                }
              }
              driverObj.moveNext()
            }, 500)
          })
          .catch(() => driverObj.moveNext())
      },
    },
    onHighlightStarted: () => {
      const checkbox = document.querySelector<HTMLElement>(
        'thead [role="checkbox"], thead input[type="checkbox"]'
      )
      if (checkbox) {
        checkbox.setAttribute('data-tour', 'select-all-checkbox')
        checkbox.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
  },
  {
    element: '[data-tour="bulk-approve-btn"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_bulk_approve_title'),
      description: i18n.t('growth.get_started.demo_tour.step_bulk_approve_description'),
      side: 'top' as const,
      onNextClick: () => {
        const approveBtn = document.querySelector<HTMLElement>('[data-tour="bulk-approve-btn"]')
        if (approveBtn) approveBtn.click()

        setTimeout(() => {
          const approvedLabel = findApprovedPresetLabel()
          if (approvedLabel) approvedLabel.click()
          callbacks.onShowConfetti()
          setTimeout(() => {
            callbacks.onShowBookingModal()
          }, 1000)
        }, 1500)
      },
    },
    onHighlightStarted: () => {
      const bar = document.querySelector<HTMLElement>('.fixed.bottom-2.z-50')
      if (bar) {
        bar.style.zIndex = '100002'
      }
    },
  },
]

export const TASK_STEP_COUNTS: Record<string, number> = {
  filter_important: 5,
  detect_alerts: 3,
  bulk_approve: 3,
}

export function getTaskSteps(
  taskKey: string,
  driverInstance: Driver,
  callbacks?: TourCallbacks
): DriveStep[] {
  const cbs: TourCallbacks = callbacks ?? {
    onShowConfetti: () => {},
    onShowBookingModal: () => {},
    navigate: () => {},
    skipReset: () => {},
    resumeTour: () => {},
  }
  switch (taskKey) {
    case 'filter_important':
      return filterImportantSteps(driverInstance, cbs)
    case 'detect_alerts':
      return detectAlertsSteps(driverInstance, cbs)
    case 'bulk_approve':
      return bulkApproveSteps(driverInstance, cbs)
    default:
      return []
  }
}
